import { formatCurrency } from 'common/FormatUtilities';
import React from 'react';
import { useMultiCartSelector } from 'src/pages/consumer/order/components/MultiCart/MultiCartSelector';
import { ButtonColors, ButtonSizes, PrimaryButton } from 'src/shared/design-system/Buttons';
import { Paragraph } from 'src/shared/design-system/Text';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { useSelector } from 'src/store';
import { selectCart, selectTotalDiscountApplied } from 'src/store/cart/selectors';
import styled from 'styled-components';
import { Colors, Mixins, ScreenWidth, Spacing } from 'web-common/src/shared/styles';

export const MULTI_CART_TOTALS_CONTAINER_HEIGHT = 72;
export const MultiCartTotalsContainer = styled.div<{ floatForward: boolean }>`
  background: ${Colors.WHITE};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.SINGLE} ${Spacing.SINGLE_HALF};

  @media (max-width: ${ScreenWidth.SMALL}) {
    padding: ${Spacing.SINGLE};
    height: ${MULTI_CART_TOTALS_CONTAINER_HEIGHT}px;
  }

  // This sticky div appears to float forward so that content can scroll beneath it
  transition: box-shadow 200ms ease-out;
  box-shadow: ${ifProp('floatForward', `0px 2px 4px -1px ${Colors.GRAY_LITE_10}`, '0px 2px 4px -1px transparent')};
`;

const TotalsContainer = styled.div`
  ${Mixins.Flex.set('flex-start', 'center')}
`;

const TotalsWrapper = styled.div`
  ${Mixins.Flex.setColumn()}
`;

export const CheckoutButton = styled(PrimaryButton)`
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
  height: 48px;
`;

type ContainerProps = React.ComponentProps<typeof MultiCartTotalsContainer>;

interface MultiCartTotalsProps {
  isEditingOrder: boolean;
  onCheckoutClick: () => void;
}

export const MultiCartTotalsComponent: React.FC<MultiCartTotalsProps & ContainerProps> = ({
  isEditingOrder,
  onCheckoutClick,
  ...props
}) => {
  const cart = useMultiCartSelector(isEditingOrder);
  const totalPromoApplied = useSelector((state) => selectTotalDiscountApplied(selectCart(state)));
  const canCheckout =
    cart.checkoutShefIds.length > 0 ||
    cart.checkoutOrders.some((order) => order.capacityInfo.isOrderingMoreThanDeliveryDateCapacity);
  const checkoutButtonText = isEditingOrder ? 'View Updated Order' : 'Checkout';

  return (
    <MultiCartTotalsContainer {...props}>
      <TotalsWrapper>
        <TotalsContainer>
          <Paragraph size='20px' weight='700' margin='0 8px 0 0' color={Colors.GRAY_DARK_30}>
            {formatCurrency(Math.max(cart.subtotal - totalPromoApplied, 0))}
          </Paragraph>
          <Paragraph size='14px' margin='0 4px 0 0' color={Colors.GRAY_DARK_20}>
            /
          </Paragraph>
          <Paragraph size='14px' weight='500' margin='0' color={Colors.GRAY_DARK_20}>
            {cart.numAvailableCartItems} {cart.numAvailableCartItems === 1 ? 'Item' : 'Items'}
          </Paragraph>
        </TotalsContainer>
        {!!totalPromoApplied && (
          <TotalsContainer>
            <Paragraph
              size='14px'
              weight='600'
              margin='0 8px 0 0'
              color={Colors.GRAY_DARK_20}
              style={{ textDecoration: 'line-through' }}>
              {formatCurrency(cart.subtotal)}
            </Paragraph>
            <Paragraph size='14px' weight='600' margin='0' color={Colors.MINT_600}>
              Saving {formatCurrency(totalPromoApplied)}
            </Paragraph>
          </TotalsContainer>
        )}
      </TotalsWrapper>
      <CheckoutButton
        onClick={onCheckoutClick}
        size={ButtonSizes.M}
        color={canCheckout ? ButtonColors.RED : ButtonColors.GRAY}
        disabled={!canCheckout}
        data-cy='checkout-btn'>
        <Paragraph size='16px' weight='500' margin='0 auto' white bold>
          {checkoutButtonText}
        </Paragraph>
      </CheckoutButton>
    </MultiCartTotalsContainer>
  );
};

// FUTURE TODO: can't get types right for this to be used as a selector for other styled components so just wrapping it here
export const MultiCartTotals = styled(MultiCartTotalsComponent)``;
